import { logger } from '@tunasong/models'
import type { Y } from '@tunasong/sync-lib'
import { IndexeddbPersistence } from '@tunasong/sync-lib'
import { useCallback, useEffect, useRef, useState } from 'react'
import invariant from 'tiny-invariant'
import { indexeddbCacheName } from './db.js'

interface useYjsDBSyncProps {
  entityId: string
  doc: Y.Doc
  disabled?: boolean
}
/** Sync from IndexedDB */
export const useYjsDBSync = (props: useYjsDBSyncProps) => {
  const { doc, entityId, disabled = false } = props

  invariant(entityId, `useYjsDBSync without entityId specified`)
  const markAsReady = useRef<(() => void) | null>(null)
  const readyPromise = useRef<Promise<void> | null>(null)
  useEffect(() => {
    readyPromise.current = new Promise(resolve => {
      markAsReady.current = resolve
    })
  }, [])

  /** `true` when the doc has been synced with local persistence, or syncing is disabled */
  const [isSynced, setIsSynced] = useState(disabled ? true : false)
  const idbRef = useRef<IndexeddbPersistence | null>(null)

  /** Start provider first to ensure that the SyncHandler will record changes from Indexeddb */
  useEffect(() => {
    if (disabled) {
      return
    }

    const idb = new IndexeddbPersistence({ cacheName: indexeddbCacheName(entityId), doc })
    const onSynced = () => {
      logger.debug(`useYjsDBSync: Synced with local IndexedDB!`)
      setIsSynced(true)
    }

    idb.on('synced', onSynced)
    idbRef.current = idb

    invariant(markAsReady.current, `useYjsDBSync: markAsReady.current is not set`)
    markAsReady.current()

    logger.debug(`useYjsDBSync: marking as ready`)

    /**
     * Cleanup when closing. Always destroy — a pending `_storeTimeoutId` is a
     * debounced *compaction* (storeState), NOT a pending data write: every update
     * is persisted immediately in `_storeUpdate`, and `destroy()` clears the
     * timeout itself. Skipping destroy here orphaned the persistence's
     * `doc.on('update')` observer + open IDB connection, leaking the Y.Doc per
     * rapid song-switch / setlist page-turn (GH #1652).
     */
    return () => {
      idb.off('synced', onSynced)
      void idb.destroy()
    }
  }, [disabled, doc, entityId])

  /**
   * Sync from local IndexedDB store to doc
   */
  const sync = useCallback(async () => {
    invariant(markAsReady.current, `useYjsDBSync: markAsReady.current is not set`)
    logger.debug(`useYjsDBSync: waiting for IndexedDB sync to be ready...`, { promise: readyPromise.current })
    await readyPromise.current
    invariant(idbRef.current, `useYjsDBSync: idbRef.current is not set`)

    logger.debug(`useYjsDBSync: starting sync with local IndexedDB...`)

    return idbRef.current.sync()
  }, [])

  return { isSynced, sync }
}
